import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useXFetch } from '~/shared/utils/useXFetch';
import {
  LastDayResponse,
  SavingStatsResponse,
  VolumeCountResponse,
  VolumesCapacityResponse,
} from '~/shared/types/api';
import Card from '~/shared/components/Card';
import { DEFAULT_REFETCH_INTERVAL_MS } from '~/shared/constants/polling';
import { useAuthContext } from '~/shared/context/useAuthContext';
import {
  getOrgAccountsScopeValue,
  getVolumeDataQueryScopeParts,
} from '~/shared/utils/organizationUtils';
import {
  formatCompactCurrency,
  formatGiB,
  formatStorageChangePercent,
  formatVolumeCount,
} from '~/shared/utils/formatNumber';
import { KPI_FILTER_CONFIGS } from '~/shared/constants/kpiFilters';

import { buildVolumeQueryUrl } from '../helpers';

import AverageUtilizationCard from './AverageUtilizationCard';
import AutoscalingStorageSizeCard from './AutoscalingStorageSizeCard';
import MonthlySavingsCard from './MonthlySavingCard';

const baseUrl = `/api/v1/volumes`;

export default function KPICards({
  queryUrl,
  accountsScope,
}: {
  queryUrl: string;
  accountsScope?: string;
}) {
  const api = useXFetch();
  const { accounts, activeTenantId } = useAuthContext();

  const resolvedAccountsScope = accountsScope ?? getOrgAccountsScopeValue(accounts, activeTenantId);

  const volumeScopeKey = useMemo(
    () => getVolumeDataQueryScopeParts(accounts, activeTenantId),
    [accounts, activeTenantId]
  );

  const autoscalingQueryString = useMemo(
    () =>
      buildVolumeQueryUrl(KPI_FILTER_CONFIGS.AUTOSCALING_ON, {
        accountsScope: resolvedAccountsScope,
      }),
    [resolvedAccountsScope]
  );

  const monitoredQueryString = useMemo(
    () =>
      buildVolumeQueryUrl(KPI_FILTER_CONFIGS.MONITORED, {
        accountsScope: resolvedAccountsScope,
      }),
    [resolvedAccountsScope]
  );

  const {
    data: savingStatsData,
    isLoading: isLoadingSavingStats,
    error: errorSavingStats,
  } = useQuery({
    queryKey: [queryUrl],
    queryFn: () => api<SavingStatsResponse>(queryUrl),
    refetchOnWindowFocus: false,
    refetchInterval: (query) => (query.state.data ? DEFAULT_REFETCH_INTERVAL_MS : false),
  });

  const { data, isLoading: isLoadingAvgUtilization } = useQuery({
    queryKey: ['getLastDay', resolvedAccountsScope ?? 'self'],
    queryFn: () => {
      const params = new URLSearchParams();
      if (resolvedAccountsScope) {
        params.set('accounts', resolvedAccountsScope);
      }
      return api<LastDayResponse>(
        params.toString()
          ? `/api/v1/opt-log/last-day?${params.toString()}`
          : '/api/v1/opt-log/last-day'
      );
    },
    refetchOnWindowFocus: false,
    refetchInterval: (query) => (query.state.data ? DEFAULT_REFETCH_INTERVAL_MS : false),
  });

  const { data: monitoredVolumeData, isLoading: isLoadingMonitoredCount } =
    useQuery<VolumeCountResponse>({
      queryKey: ['monitoredVolumeCount', KPI_FILTER_CONFIGS.MONITORED, ...volumeScopeKey],
      queryFn: () => api<VolumeCountResponse>(`${baseUrl}/count?${monitoredQueryString}`),
      refetchOnWindowFocus: false,
      refetchInterval: (query) => (query.state.data ? DEFAULT_REFETCH_INTERVAL_MS : false),
    });

  const { data: autoscalingVolumeData, isLoading: isLoadingAutoscalingCount } =
    useQuery<VolumeCountResponse>({
      queryKey: ['autoscalingVolumeCount', KPI_FILTER_CONFIGS.AUTOSCALING_ON, ...volumeScopeKey],
      queryFn: () => api<VolumeCountResponse>(`${baseUrl}/count?${autoscalingQueryString}`),
      refetchOnWindowFocus: false,
      refetchInterval: (query) => (query.state.data ? DEFAULT_REFETCH_INTERVAL_MS : false),
    });

  const { data: autoscalingCapacityData, isLoading: isLoadingAutoscalingCapacity } =
    useQuery<VolumesCapacityResponse>({
      queryKey: ['autoscalingCapacity', KPI_FILTER_CONFIGS.AUTOSCALING_ON, ...volumeScopeKey],
      queryFn: () => api<VolumesCapacityResponse>(`${baseUrl}/capacity?${autoscalingQueryString}`),
      refetchOnWindowFocus: false,
      refetchInterval: (query) => (query.state.data ? DEFAULT_REFETCH_INTERVAL_MS : false),
    });

  const { lastDay } = data || {};

  const { currentEstimatedMonthlySavings, PotentialEstimatedMonthlySaving } = savingStatsData || {};

  if (errorSavingStats || (!savingStatsData && !isLoadingSavingStats)) {
    return <EmptyState />;
  }

  const avgUtilNum = lastDay?.avgUtilization;
  const avgUtilStr = typeof avgUtilNum === 'number' ? `${avgUtilNum.toFixed(0)} %` : 'N/A';

  const hasAutoscalingCount = typeof autoscalingVolumeData?.count === 'number';
  const hasAutoscalingOriginal = typeof autoscalingCapacityData?.capacity === 'number';
  const hasAutoscalingCurrent = typeof autoscalingCapacityData?.datafyCapacity === 'number';
  const isAutoscalingLoading =
    isLoadingAutoscalingCount ||
    isLoadingAutoscalingCapacity ||
    (!hasAutoscalingCount && !hasAutoscalingOriginal && !hasAutoscalingCurrent);

  const originalSizeGiB = hasAutoscalingOriginal ? autoscalingCapacityData.capacity : undefined;
  const currentSizeGiB = hasAutoscalingCurrent ? autoscalingCapacityData.datafyCapacity : undefined;

  const monitoredVolumeCount =
    typeof monitoredVolumeData?.count === 'number'
      ? formatVolumeCount(monitoredVolumeData.count)
      : undefined;

  return (
    <div className="w-full flex gap-[10px] items-start px-6">
      <AverageUtilizationCard
        size={avgUtilStr}
        volumeCount={monitoredVolumeCount}
        loading={isLoadingAvgUtilization || isLoadingMonitoredCount}
      />
      <AutoscalingStorageSizeCard
        originalSize={hasAutoscalingOriginal ? formatGiB(originalSizeGiB) : 'N/A'}
        currentSize={hasAutoscalingCurrent ? formatGiB(currentSizeGiB) : 'N/A'}
        changePercent={formatStorageChangePercent(originalSizeGiB, currentSizeGiB)}
        volumeCount={
          hasAutoscalingCount ? formatVolumeCount(autoscalingVolumeData.count) : 'N/A Volumes'
        }
        loading={isAutoscalingLoading}
      />
      <MonthlySavingsCard
        savings={formatCompactCurrency(currentEstimatedMonthlySavings ?? 0)}
        potential={formatCompactCurrency(PotentialEstimatedMonthlySaving ?? 0)}
        percentage={
          PotentialEstimatedMonthlySaving && currentEstimatedMonthlySavings
            ? Math.round((currentEstimatedMonthlySavings / PotentialEstimatedMonthlySaving) * 100)
            : 0
        }
        loading={isLoadingSavingStats}
      />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="px-[30px] ">
      <Card className="w-full h-[150px]  flex justify-center items-center text-[12px] ">
        No Results
      </Card>
    </div>
  );
}
